/**
 * Service de génération de PDF pour les factures
 *
 * Ce service génère des factures professionnelles en PDF
 * avec les informations de la société OMAD SERVICES
 */

import { getCompanyInfo, getDefaultCompanyInfo } from './companyInfoService';
import { db } from '../firebase';

/**
 * Formate une date Firestore Timestamp
 */
const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Formate un montant en euros
 */
const formatEuro = (montant) => {
  if (typeof montant === 'undefined' || montant === null) return '0,00 €';
  return `${montant.toFixed(2).replace('.', ',')} €`;
};

/**
 * Obtient le libellé du statut
 */
const getLibelleStatut = (statut) => {
  switch (statut) {
    case 'generée': return 'Générée';
    case 'envoyée': return 'Envoyée';
    case 'payée': return 'Payée';
    case 'en_attente': return 'En attente';
    case 'annulée': return 'Annulée';
    default: return statut;
  }
};

/**
 * Génère le HTML d'une facture pour conversion en PDF
 * @param {Object} invoice - Données de la facture
 * @param {Object} companyInfo - Informations de la société
 * @returns {string} HTML de la facture
 */
export const genererHTMLFacture = async (invoice, companyInfo = null) => {
  // Récupérer les informations de la société si non fournies
  if (!companyInfo) {
    try {
      companyInfo = await getCompanyInfo(db);
    } catch (error) {
      console.warn('Impossible de récupérer les infos société, utilisation des valeurs par défaut');
      companyInfo = getDefaultCompanyInfo();
    }
  }

  console.log('PDF Facture - Génération pour facture:', invoice.id);

  return `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Facture ${invoice.id} - ${companyInfo.denominationSociale}</title>
    <style>
        /* Styles pour l'impression PDF */
        @page {
            margin: 20mm;
            size: A4;
        }

        body {
            font-family: 'Arial', sans-serif;
            line-height: 1.4;
            color: #333;
            margin: 0;
            padding: 0;
            font-size: 12px;
        }

        .container {
            max-width: 210mm;
            margin: 0 auto;
            padding: 0;
        }

        /* En-tête */
        .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 20px;
            border-bottom: 2px solid #3b82f6;
            padding-bottom: 15px;
        }

        .company-info {
            flex: 1;
        }

        .company-logo {
            width: 60px;
            height: 60px;
            max-width: 60px;
            max-height: 60px;
            margin-bottom: 10px;
            object-fit: contain;
            display: block;
        }

        .company-name {
            font-size: 18px;
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 5px;
        }

        .company-details {
            font-size: 10px;
            color: #6b7280;
            line-height: 1.3;
        }

        .invoice-info {
            text-align: right;
        }

        .invoice-title {
            font-size: 24px;
            font-weight: bold;
            color: #3b82f6;
            margin-bottom: 5px;
        }

        .invoice-number {
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 5px;
        }

        /* Sections */
        .section {
            margin-bottom: 20px;
        }

        .section-title {
            font-size: 14px;
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 10px;
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 5px;
        }

        /* Informations client */
        .client-info {
            padding: 0;
            margin: 0;
        }

        .client-name {
            font-weight: bold;
            font-size: 14px;
            margin-bottom: 8px;
            color: #1f2937;
        }

        .client-details {
            font-size: 11px;
            line-height: 1.5;
            color: #374151;
        }

        .client-details div {
            margin-bottom: 3px;
        }

        /* Période de facturation */
        .period-box {
            background-color: #f1f5f9;
            padding: 15px;
            border-radius: 5px;
            margin-bottom: 20px;
        }

        .period-title {
            font-weight: bold;
            font-size: 12px;
            margin-bottom: 8px;
            color: #1f2937;
        }

        /* Table des détails */
        .details-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
        }

        .details-table th {
            background-color: #f1f5f9;
            padding: 8px;
            text-align: left;
            font-weight: bold;
            border: 1px solid #e2e8f0;
            font-size: 10px;
        }

        .details-table td {
            padding: 8px;
            border: 1px solid #e2e8f0;
            font-size: 10px;
        }

        .text-right {
            text-align: right;
        }

        .text-center {
            text-align: center;
        }

        /* Rendez-vous détails */
        .rdv-details {
            max-height: 300px;
            overflow-y: auto;
            font-size: 9px;
            background-color: #f9fafb;
            padding: 10px;
            margin-top: 10px;
            border-radius: 5px;
        }

        .rdv-item {
            padding: 5px 0;
            border-bottom: 1px solid #e5e7eb;
        }

        /* Totaux */
        .totals {
            width: 50%;
            margin-left: auto;
            margin-top: 20px;
        }

        .total-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px solid #e5e7eb;
        }

        .total-final {
            font-weight: bold;
            font-size: 16px;
            border-top: 2px solid #3b82f6;
            margin-top: 5px;
            padding-top: 10px;
            color: #3b82f6;
        }

        /* Informations de paiement */
        .payment-info {
            margin-top: 30px;
            padding: 15px;
            background-color: #f1f5f9;
            border-radius: 5px;
        }

        .payment-info-title {
            font-weight: bold;
            font-size: 12px;
            margin-bottom: 10px;
            color: #1f2937;
        }

        /* Pied de page */
        .footer {
            margin-top: 40px;
            padding-top: 15px;
            border-top: 1px solid #e5e7eb;
            font-size: 9px;
            color: #6b7280;
            text-align: center;
        }

        /* Utilitaires */
        .no-break {
            page-break-inside: avoid;
        }

        .font-bold {
            font-weight: bold;
        }

        .status-badge {
            display: inline-block;
            padding: 3px 8px;
            border-radius: 3px;
            font-size: 10px;
            font-weight: bold;
        }

        .status-generee {
            background-color: #dbeafe;
            color: #1e40af;
        }

        .status-envoyee {
            background-color: #fef3c7;
            color: #92400e;
        }

        .status-payee {
            background-color: #d1fae5;
            color: #065f46;
        }

        .status-annulee {
            background-color: #fee2e2;
            color: #991b1b;
        }
    </style>
</head>
<body>
    <div class="container">
        <!-- En-tête -->
        <div class="header no-break">
            <div class="company-info">
                ${companyInfo.logoUrl ? `<img src="${companyInfo.logoUrl}" alt="Logo ${companyInfo.denominationSociale}" class="company-logo">` : ''}
                <div class="company-name">${companyInfo.denominationSociale} (${companyInfo.sigle})</div>
                <div class="company-details">
                    ${companyInfo.formeJuridique}<br>
                    Capital : ${companyInfo.capitalSocial}<br>
                    ${companyInfo.siegeSocial}<br>
                    SIREN : ${companyInfo.siren} - SIRET : ${companyInfo.siret}<br>
                    ${companyInfo.numeroSAP ? `SAP : ${companyInfo.numeroSAP}<br>` : ''}
                    ${companyInfo.immatriculation ? `${companyInfo.immatriculation}<br>` : ''}
                    ${companyInfo.telephone ? `Tél : ${companyInfo.telephone}` : ''}
                    ${companyInfo.email ? ` - Email : ${companyInfo.email}` : ''}
                    ${companyInfo.siteWeb ? ` - Site : ${companyInfo.siteWeb}` : ''}
                </div>
            </div>
            <div class="invoice-info">
                <div class="invoice-title">FACTURE</div>
                <div class="invoice-number">N° ${invoice.id}</div>
                <div>Date d'émission : ${formatDate(invoice.dateGeneration)}</div>
                <div class="status-badge status-${invoice.statut}">${getLibelleStatut(invoice.statut)}</div>
            </div>
        </div>

        <!-- Informations client -->
        <div class="section no-break">
            <div class="section-title">FACTURER À</div>
            <div class="client-info">
                <div class="client-name">${invoice.client?.nom || 'Client inconnu'}</div>
                <div class="client-details">
                    ${invoice.client?.email ? `<div><strong>Email :</strong> ${invoice.client.email}</div>` : ''}
                    ${invoice.client?.telephone ? `<div><strong>Téléphone :</strong> ${invoice.client.telephone}</div>` : ''}
                    <div><strong>Client ID :</strong> ${invoice.clientId}</div>
                </div>
            </div>
        </div>

        <!-- Période de facturation -->
        <div class="period-box no-break">
            <div class="period-title">Période de facturation</div>
            <div>Du ${formatDate(invoice.periode?.debut)} au ${formatDate(invoice.periode?.fin)}</div>
            <div style="font-size: 11px; color: #6b7280; margin-top: 5px;">
                ${invoice.periode?.libelle || 'Facturation mensuelle'}
            </div>
        </div>

        <!-- Détails de la facturation -->
        <div class="section">
            <div class="section-title">DÉTAIL DE LA FACTURATION</div>
            <table class="details-table">
                <thead>
                    <tr>
                        <th>Description</th>
                        <th class="text-center">Quantité</th>
                        <th class="text-right">Prix unitaire</th>
                        <th class="text-right">Total</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <div class="font-bold">Frais de mandat bénéficiaire</div>
                            <div style="font-size: 9px; color: #6b7280; margin-top: 3px;">
                                Période : ${invoice.periode?.libelle || 'N/A'}<br>
                                Nombre de rendez-vous : ${invoice.calcul?.nombreRendezVous || 0}<br>
                                ${invoice.calcul?.contrat ? `Contrat : ${invoice.calcul.contrat.typeContrat || 'Standard'}` : ''}
                            </div>
                        </td>
                        <td class="text-center">
                            <div class="font-bold">${invoice.calcul?.totalHeures?.toFixed(2) || '0.00'} heures</div>
                        </td>
                        <td class="text-right">
                            ${formatEuro(invoice.calcul?.fraisMandatUnitaire || 0)} / heure
                        </td>
                        <td class="text-right font-bold">
                            ${formatEuro(invoice.calcul?.montantTotal || 0)}
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Détails des rendez-vous -->
            ${invoice.calcul?.rendezVousDetails && invoice.calcul.rendezVousDetails.length > 0 ? `
            <div style="margin-top: 15px;">
                <div style="font-weight: bold; font-size: 11px; margin-bottom: 8px;">
                    Détail des rendez-vous (${invoice.calcul.rendezVousDetails.length} rendez-vous)
                </div>
                <div class="rdv-details">
                    ${invoice.calcul.rendezVousDetails.map((rdv, index) => {
                      // Formater la date correctement
                      let dateStr = 'Date inconnue';
                      if (rdv.date) {
                        try {
                          const d = rdv.date.toDate ? rdv.date.toDate() : new Date(rdv.date);
                          dateStr = d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                        } catch (e) {
                          dateStr = 'Date invalide';
                        }
                      }

                      // Formater les notes (gérer si c'est un objet)
                      let notesStr = '';
                      if (rdv.notes) {
                        if (typeof rdv.notes === 'string') {
                          notesStr = rdv.notes;
                        } else if (typeof rdv.notes === 'object') {
                          // Si c'est un objet, essayer d'extraire le texte
                          notesStr = rdv.notes.text || rdv.notes.message || rdv.notes.description || '';
                        }
                        // Nettoyer et limiter la longueur
                        if (notesStr && notesStr.length > 50) {
                          notesStr = notesStr.substring(0, 50) + '...';
                        }
                      }

                      return `
                        <div class="rdv-item">
                            <strong>${index + 1}.</strong>
                            ${dateStr} -
                            ${rdv.heures || '0'} heures
                        </div>
                      `;
                    }).join('')}
                </div>
            </div>
            ` : ''}
        </div>

        <!-- Totaux -->
        <div class="totals">
            <div class="total-row">
                <span>Sous-total HT</span>
                <span class="font-bold">${formatEuro(invoice.calcul?.montantTotal || 0)}</span>
            </div>
            <div class="total-row">
                <span>TVA (Non applicable)</span>
                <span class="font-bold">0,00 €</span>
            </div>
            <div class="total-row total-final">
                <span>TOTAL TTC</span>
                <span>${formatEuro(invoice.calcul?.montantTotal || 0)}</span>
            </div>
        </div>

        <!-- Informations de paiement -->
        <div class="payment-info no-break">
            <div class="payment-info-title">Informations de paiement</div>
            <div style="font-size: 10px; line-height: 1.5;">
                ${invoice.statut === 'payée' ? `
                    <div style="color: #065f46; font-weight: bold;">✓ Facture payée</div>
                    ${invoice.paidAt ? `<div>Date de paiement : ${formatDate(invoice.paidAt)}</div>` : ''}
                ` : `
                    <div>Cette facture peut être réglée en ligne via votre espace client.</div>
                    <div>Connectez-vous à votre compte pour effectuer le paiement de manière sécurisée.</div>
                `}
                <div style="margin-top: 10px;">
                    <strong>Contact :</strong> ${companyInfo.email} - ${companyInfo.telephone}
                </div>
            </div>
        </div>

        <!-- Pied de page -->
        <div class="footer">
            <div>Page 1</div>
        </div>
    </div>
</body>
</html>
  `;
};

/**
 * Génère un PDF à partir du HTML d'une facture
 * @param {string} html - HTML de la facture
 * @param {string} filename - Nom du fichier PDF
 * @returns {Promise<void>}
 */
export const genererPDFFacture = async (html, filename = 'facture.pdf') => {
  // Créer une iframe cachée pour générer le PDF
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';

  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    // Attendre que le contenu soit chargé
    await new Promise(resolve => {
      if (iframe.contentWindow.document.readyState === 'complete') {
        resolve();
      } else {
        iframe.contentWindow.addEventListener('load', resolve);
      }
    });

    // Petite attente supplémentaire pour s'assurer que les styles sont appliqués
    await new Promise(resolve => setTimeout(resolve, 500));

    // Lancer l'impression
    iframe.contentWindow.focus();
    iframe.contentWindow.print();

  } finally {
    // Nettoyer après un délai
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);
  }
};

/**
 * Télécharge une facture en PDF
 * @param {Object} invoice - Données de la facture
 * @param {Object} companyInfo - Informations de la société (optionnel)
 * @returns {Promise<void>}
 */
export const telechargerFacturePDF = async (invoice, companyInfo = null) => {
  try {
    console.log('📥 Génération PDF pour facture:', invoice.id);

    // Récupérer les informations de la société si non fournies
    if (!companyInfo) {
      try {
        companyInfo = await getCompanyInfo(db);
      } catch (error) {
        console.warn('Impossible de récupérer les infos société, utilisation des valeurs par défaut');
        companyInfo = getDefaultCompanyInfo();
      }
    }

    const html = await genererHTMLFacture(invoice, companyInfo);

    // Formater le nom du client pour le nom de fichier (enlever espaces et caractères spéciaux)
    const clientName = (invoice.client?.nom || 'Client')
      .replace(/\s+/g, '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .substring(0, 20); // Limiter à 20 caractères

    // Nom de fichier format: {Société}_{NuméroFacture}_{NomClient}.pdf
    const filename = `${companyInfo.sigle}_${invoice.id}_${clientName}.pdf`;

    await genererPDFFacture(html, filename);

    console.log('✅ PDF généré avec succès');
  } catch (error) {
    console.error('❌ Erreur génération PDF:', error);
    throw error;
  }
};

/**
 * Génère un buffer PDF pour envoi par email
 * @param {Object} invoice - Données de la facture
 * @param {Object} companyInfo - Informations de la société (optionnel)
 * @returns {Promise<Blob>} Blob du PDF
 */
export const genererPDFFactureBuffer = async (invoice, companyInfo = null) => {
  try {
    console.log('📄 Génération buffer PDF pour facture:', invoice.id);

    // Récupérer les informations de la société si non fournies
    if (!companyInfo) {
      try {
        companyInfo = await getCompanyInfo(db);
      } catch (error) {
        console.warn('Impossible de récupérer les infos société, utilisation des valeurs par défaut');
        companyInfo = getDefaultCompanyInfo();
      }
    }

    // Générer le HTML
    const html = await genererHTMLFacture(invoice, companyInfo);

    // Formater le nom du client pour le nom de fichier
    const clientName = (invoice.client?.nom || 'Client')
      .replace(/\s+/g, '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .substring(0, 20);

    // Créer un iframe invisible pour contenir le HTML avec tous les styles
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.top = '0';
    iframe.style.width = '210mm';  // A4 width
    iframe.style.height = '297mm'; // A4 height
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    // Écrire le HTML complet dans l'iframe
    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(html);
    iframeDoc.close();

    // Attendre que le contenu soit chargé
    await new Promise(resolve => {
      if (iframe.contentWindow.document.readyState === 'complete') {
        resolve();
      } else {
        iframe.contentWindow.addEventListener('load', resolve);
      }
    });

    // Attendre que les styles soient appliqués
    await new Promise(resolve => setTimeout(resolve, 500));

    // Importer html2pdf dynamiquement
    const html2pdf = (await import('html2pdf.js')).default;

    // Options de génération PDF optimisées
    const options = {
      margin: [10, 10, 10, 10],
      filename: `${companyInfo.sigle}_${invoice.id}_${clientName}.pdf`,
      image: {
        type: 'jpeg',
        quality: 1.0  // Qualité maximale
      },
      html2canvas: {
        scale: 3,  // Augmenté de 2 à 3 pour meilleur rendu
        useCORS: true,
        logging: false,
        letterRendering: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        imageTimeout: 0,
        removeContainer: true,
        foreignObjectRendering: false,
        scrollY: 0,
        scrollX: 0,
        windowWidth: 794,  // A4 width en px (210mm à 96 DPI)
        windowHeight: 1123  // A4 height en px (297mm à 96 DPI)
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
        compress: true,
        precision: 16,
        userUnit: 1.0
      },
      pagebreak: {
        mode: ['avoid-all', 'css', 'legacy'],
        before: '.no-break',
        after: [],
        avoid: ['.no-break']
      }
    };

    // Générer le PDF à partir du body de l'iframe
    const pdfBlob = await html2pdf()
      .set(options)
      .from(iframeDoc.body)
      .outputPdf('blob');

    // Créer un vrai Blob PDF
    const blob = new Blob([pdfBlob], { type: 'application/pdf' });

    // Nettoyer
    document.body.removeChild(iframe);

    console.log('✅ Buffer PDF généré:', blob.size, 'bytes');
    return blob;

  } catch (error) {
    console.error('❌ Erreur génération buffer PDF:', error);
    throw error;
  }
};

export default {
  genererHTMLFacture,
  genererPDFFacture,
  telechargerFacturePDF,
  genererPDFFactureBuffer
};
